"use client";

import { t } from "@/lib/localization";
import { TextBlock } from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import RichText from "./richText";
import { wrapInTag } from "../../../lib/titleHtml";
import { fieldTag } from "../../../lib/sectionRegistry";

interface QuoteVariantProps {
    block: TextBlock;
    editable: boolean;
}

const hasContent = (html: string) =>
    !!html && html.replace(/<[^>]*>/g, "").trim().length > 0;

/** A single quotation-mark glyph, drawn twice to form the double mark. */
const GLYPH =
    "M7.28911 22.5059C5.29675 22.5059 3.59595 21.7284 2.18672 20.1803C1.51334 19.4583 0.985745 18.5906 0.590049 17.5562C0.194353 16.5288 0 15.4875 0 14.4462C0 12.2455 0.715006 10.0171 2.15201 7.76098C3.58901 5.50482 5.51196 3.58882 7.9139 2.01298C9.95486 0.673164 11.5515 0 12.7039 0C13.169 0 13.5508 0.138628 13.8493 0.416309C14.1478 0.693991 14.3006 1.06192 14.3006 1.52703C14.3006 2.22124 13.8632 2.79743 12.9816 3.26254C10.8781 4.35244 9.33701 5.42846 8.36513 6.49059C7.44184 7.4833 6.97672 8.21221 6.97672 8.67733C6.97672 9.14244 7.32382 9.38541 8.01803 9.47566L8.46926 9.54508L8.98991 9.57979C10.4963 9.80888 11.7181 10.51 12.6553 11.6832C13.5925 12.8495 14.0645 14.2726 14.0645 15.9387C14.0645 17.6048 13.4883 19.132 12.329 20.3816C11.01 21.7908 9.33007 22.4989 7.29605 22.4989L7.28911 22.5059Z";

function QuoteMark({ className }: { className?: string }) {
    return (
        <svg
            width="40"
            height="40"
            viewBox="0 0 40 40"
            fill="none"
            aria-hidden
            className={className}
        >
            <path
                fill="currentColor"
                transform="translate(4.53 8.75) scale(0.955 0.978)"
                d={GLYPH}
            />
            <path
                fill="currentColor"
                transform="translate(21.17 8.75) scale(0.955 0.978)"
                d={GLYPH}
            />
        </svg>
    );
}

/**
 * Pull-quote (Figma "Quote" section): a centered italic body + a muted author
 * line inside a bordered, themed card, with a decorative double quote mark in
 * the top-start and bottom-end corners. Body and author are each their own
 * rich-text region so they're edited independently.
 */
export default function QuoteVariant({ block, editable }: QuoteVariantProps) {
    const quoteTag = fieldTag("text", "quote");
    const authorTag = fieldTag("text", "author");
    const updateBlock = useLomdaV3Editor((s) => s.updateBlock);

    const data = block.data;
    if (data.variant !== "quote") return null;

    const showAuthor = editable || hasContent(data.authorName);

    return (
        <figure className="relative isolate flex flex-col items-center gap-10 rounded-[24px] border border-[hsl(var(--lomda-border))] bg-[hsl(var(--lomda-background))] px-8 py-10 text-center md:px-20">
            <QuoteMark className="absolute -top-4 start-8 rotate-180 text-[hsl(var(--lomda-icons))]" />
            <QuoteMark className="absolute -bottom-4 end-8 text-[hsl(var(--lomda-icons))]" />

            <div className="flex w-full flex-col gap-4">
                <div className="[&_.lomda-v3-rich-text]:text-center">
                    {/* Figma: the quote body is H2 + italic. Both are real
                        (tag + mark) so the topbar shows them selected and
                        changing them actually applies — the old
                        `lomda-v3-quote-body` class forced h2/italic onto every
                        tag, which is why the type dropdown did nothing. */}
                    <RichText
                        initialHtml={data.quote || wrapInTag(quoteTag)}
                        defaultTag={quoteTag}
                        placeholder={t("lomda-v3-editor.quote.placeholder")}
                        editable={editable}
                        editorClassName="text-[hsl(var(--lomda-text))]"
                        useGlobalToolbar
                        centerPlaceholder
                        defaultItalic
                        onCommit={(quote) => updateBlock(block.id, { quote })}
                    />
                </div>

                {showAuthor && (
                    <div className="[&_.lomda-v3-rich-text]:text-center">
                        {/* Figma: author = H6, Muted text. */}
                        <RichText
                            initialHtml={
                                data.authorName || wrapInTag(authorTag)
                            }
                            defaultTag={authorTag}
                            placeholder={
                                editable
                                    ? t("lomda-v3-editor.quote.author-name")
                                    : undefined
                            }
                            editable={editable}
                            editorClassName="text-[hsl(var(--lomda-muted-text))]"
                            useGlobalToolbar
                            centerPlaceholder
                            onCommit={(authorName) =>
                                updateBlock(block.id, { authorName })
                            }
                        />
                    </div>
                )}
            </div>
        </figure>
    );
}
